import { useCallback, useState } from "react";
import { StripeElementChangeEvent } from "@stripe/stripe-js";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";

// eslint-disable-next-line import/no-cycle
import StripeElement from "../StripeElement/StripeElement.web";

/**
 * A containing element for Stripe.JS to inject their elements into
 */
function StripeCardExpiryElement({
  onValidField,
  onInvalidField,
}: {
  /**
   * Fires when a value is well-formed and potentially complete.
   * It is not an indicator of whether a customer is done with their input --
   * it only indicates that the Element contains a potentially complete, well-formed value.
   */
  onValidField?: () => void;
  /**
   * Fires when a value is deemed invalid
   */
  onInvalidField?: () => void;
}) {
  const { t } = useTranslation("checkout");
  const [error, setError] = useState("");

  const handleChange = useCallback(
    (evt: StripeElementChangeEvent) => {
      // Event handling is unique per element
      if (evt.error) {
        setError(evt.error.message);
      } else if (!evt.error) {
        setError("");
      }

      if (evt.complete) {
        onValidField?.();
      } else {
        onInvalidField?.();
      }
    },
    [onValidField, onInvalidField]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="sm">
          {t("Expiration")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={!error ? "border" : "red.600"}
        borderRadius="sm"
        px="sm"
        bg="bg"
      >
        <StripeElement
          element="cardExpiry"
          onChange={handleChange}
          elementProps={{
            placeholder: t("MM / YY"),
          }}
        />
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={2}
          data-testid="stripe-element-cardExpiry-error"
        >
          {error}
        </Text>
      </Box>
    </Box>
  );
}

export default StripeCardExpiryElement;
